import { InteractivePythonBlock } from '../components/ui/InteractivePythonBlock';

export default function PortfolioManagementModernPortfolioTheoryFull() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl font-bold mb-6">Modern Portfolio Theory</h1>
          <p className="text-2xl opacity-90 mb-4">Complete Professional Manual</p>
          <p className="text-xl opacity-80 mb-8">Portfolio Management Series - Advanced Level</p>
          <div className="flex flex-wrap items-center gap-4">
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-semibold">15 Interactive Python Examples</span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-semibold">Real-World Case Studies</span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-semibold">Downloadable Code</span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-semibold">Professional Level</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        
        {/* Table of Contents */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-8 mb-12 rounded-lg">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">📚 Complete Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-6 text-blue-800">
            <div>
              <h3 className="font-bold mb-3">Part I: Foundations</h3>
              <ul className="space-y-1 text-sm">
                <li>• Chapter 1: Introduction to MPT</li>
                <li>• Chapter 2: Mathematical Framework</li>
                <li>• Chapter 3: Risk and Return Concepts</li>
                <li>• Chapter 4: Correlation and Covariance</li>
              </ul>
              
              <h3 className="font-bold mb-3 mt-6">Part II: Portfolio Construction</h3>
              <ul className="space-y-1 text-sm">
                <li>• Chapter 5: Two-Asset Portfolios</li>
                <li>• Chapter 6: Multi-Asset Optimization</li>
                <li>• Chapter 7: Efficient Frontier</li>
                <li>• Chapter 8: Capital Allocation Line</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-3">Part III: Advanced Topics</h3>
              <ul className="space-y-1 text-sm">
                <li>• Chapter 9: Black-Litterman Model</li>
                <li>• Chapter 10: Risk Budgeting</li>
                <li>• Chapter 11: Factor Models</li>
                <li>• Chapter 12: Behavioral Considerations</li>
              </ul>
              
              <h3 className="font-bold mb-3 mt-6">Part IV: Practical Implementation</h3>
              <ul className="space-y-1 text-sm">
                <li>• Chapter 13: Real-World Constraints</li>
                <li>• Chapter 14: Performance Attribution</li>
                <li>• Chapter 15: Case Studies</li>
                <li>• Chapter 16: Python Implementation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Chapter 1: Introduction */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-blue-500 pb-4">Chapter 1: Introduction to Modern Portfolio Theory</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Modern Portfolio Theory (MPT), developed by Harry Markowitz in his groundbreaking 1952 paper "Portfolio Selection," 
              revolutionized the field of investment management by providing the first rigorous mathematical framework for portfolio construction. 
              For this work, Markowitz shared the 1990 Nobel Prize in Economic Sciences, cementing MPT's place as one of the most important 
              contributions to financial theory.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Prior to Markowitz's work, investment decisions were largely based on intuition and rules of thumb. MPT introduced the 
              revolutionary concept that portfolio risk is not simply the weighted average of individual asset risks, but depends critically 
              on the correlations between assets. This insight laid the foundation for modern diversification theory and quantitative 
              portfolio management.
            </p>
          </div>

          {/* Key Innovation Box */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 p-6 mb-8">
            <h3 className="text-xl font-bold text-yellow-900 mb-3">🎯 Key Innovation</h3>
            <p className="text-yellow-800 text-lg">
              <strong>The Portfolio Effect:</strong> By combining assets with less than perfect correlation, investors can achieve 
              the same expected return with lower risk, or higher expected return for the same level of risk. This is the essence 
              of diversification benefit.
            </p>
          </div>

          {/* Historical Context */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">📈 Before MPT (Pre-1952)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Investment decisions based on intuition</li>
                <li>• "Don't put all eggs in one basket" - but no mathematical framework</li>
                <li>• Focus on individual security analysis</li>
                <li>• No systematic approach to portfolio construction</li>
                <li>• Risk measured by volatility of individual assets</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-blue-900 mb-4">🔬 After MPT (Post-1952)</h3>
              <ul className="space-y-2 text-blue-800">
                <li>• Mathematical optimization of portfolios</li>
                <li>• Quantification of diversification benefits</li>
                <li>• Risk-return trade-off formalization</li>
                <li>• Efficient frontier concept</li>
                <li>• Foundation for modern portfolio management</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Chapter 2: Mathematical Framework */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-blue-500 pb-4">Chapter 2: Mathematical Framework</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The mathematical foundation of MPT rests on several key assumptions and formulations. Understanding these mathematical 
              relationships is crucial for practical implementation and for recognizing the theory's limitations.
            </p>
          </div>

          {/* Core Assumptions */}
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
            <h3 className="text-xl font-bold text-red-900 mb-4">⚠️ Core Assumptions of MPT</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-red-800 mb-2">Market Assumptions:</h4>
                <ul className="space-y-1 text-red-700 text-sm">
                  <li>• Markets are efficient and liquid</li>
                  <li>• No transaction costs or taxes</li>
                  <li>• Unlimited borrowing/lending at risk-free rate</li>
                  <li>• All investors have same information</li>
                  <li>• Single-period investment horizon</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-red-800 mb-2">Statistical Assumptions:</h4>
                <ul className="space-y-1 text-red-700 text-sm">
                  <li>• Returns are normally distributed</li>
                  <li>• Mean and variance are constant over time</li>
                  <li>• Correlations are stable</li>
                  <li>• Investors are rational and risk-averse</li>
                  <li>• Utility depends only on mean and variance</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Mathematical Formulation */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">📐 Mathematical Formulation</h3>
            
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-900 mb-2">Portfolio Expected Return:</h4>
                <div className="bg-white p-4 rounded font-mono text-lg text-center border">
                  E(Rp) = Σ wi × E(Ri)
                </div>
                <p className="text-blue-800 text-sm mt-2">
                  Where wi is the weight of asset i, and E(Ri) is the expected return of asset i
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-bold text-green-900 mb-2">Portfolio Variance:</h4>
                <div className="bg-white p-4 rounded font-mono text-lg text-center border">
                  σp² = Σ Σ wi × wj × σij
                </div>
                <p className="text-green-800 text-sm mt-2">
                  Where σij is the covariance between assets i and j (σii = σi² for i=j)
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-bold text-purple-900 mb-2">Covariance Relationship:</h4>
                <div className="bg-white p-4 rounded font-mono text-lg text-center border">
                  σij = ρij × σi × σj
                </div>
                <p className="text-purple-800 text-sm mt-2">
                  Where ρij is the correlation coefficient between assets i and j
                </p>
              </div>
            </div>
          </div>

          {/* Interactive Example 1: Basic Math */}
          <InteractivePythonBlock 
            filename="mpt_mathematical_foundations.py" 
            title="Mathematical Foundations of MPT"
            editable={true}
          >
{`import numpy as np
import pandas as pd

print("=== MODERN PORTFOLIO THEORY: MATHEMATICAL FOUNDATIONS ===")
print()

# Define a simple 3-asset universe
assets = ['US Stocks', 'International Stocks', 'Bonds']
expected_returns = np.array([0.10, 0.08, 0.04])  # 10%, 8%, 4%
volatilities = np.array([0.20, 0.25, 0.05])      # 20%, 25%, 5%

# Correlation matrix (realistic values)
correlation_matrix = np.array([
    [1.00, 0.70, -0.10],  # US Stocks
    [0.70, 1.00, -0.05],  # International Stocks  
    [-0.10, -0.05, 1.00]  # Bonds
])

print("Asset Universe:")
print("-" * 50)
for i, asset in enumerate(assets):
    print(f"{asset:20} | Return: {expected_returns[i]:6.1%} | Vol: {volatilities[i]:6.1%}")

print("\\nCorrelation Matrix:")
print("-" * 50)
correlation_df = pd.DataFrame(correlation_matrix, 
                            index=assets, 
                            columns=['US Stk', 'Intl Stk', 'Bonds'])
print(correlation_df.round(2))

# Calculate covariance matrix
covariance_matrix = np.outer(volatilities, volatilities) * correlation_matrix

print("\\nCovariance Matrix (annualized):")
print("-" * 50)
covariance_df = pd.DataFrame(covariance_matrix, 
                           index=assets, 
                           columns=['US Stk', 'Intl Stk', 'Bonds'])
print(covariance_df.round(4))

# Example portfolio calculation
print("\\n" + "="*60)
print("PORTFOLIO CALCULATION EXAMPLE")
print("="*60)

# Equal-weighted portfolio
weights = np.array([1/3, 1/3, 1/3])
print(f"Portfolio weights: {weights}")

# Portfolio expected return
portfolio_return = np.sum(weights * expected_returns)
print(f"\\nPortfolio Expected Return: {portfolio_return:.2%}")

# Portfolio variance (step by step)
print("\\nPortfolio Variance Calculation (step by step):")
portfolio_variance = 0

for i in range(len(assets)):
    for j in range(len(assets)):
        contribution = weights[i] * weights[j] * covariance_matrix[i, j]
        portfolio_variance += contribution
        print(f"w[{i}] × w[{j}] × cov[{i},{j}] = {weights[i]:.3f} × {weights[j]:.3f} × {covariance_matrix[i,j]:.4f} = {contribution:.6f}")

portfolio_volatility = np.sqrt(portfolio_variance)

print(f"\\nTotal Portfolio Variance: {portfolio_variance:.6f}")
print(f"Portfolio Volatility: {portfolio_volatility:.2%}")

# Compare to naive diversification
naive_volatility = np.sum(weights * volatilities)
print(f"\\nNaive Diversification (weighted average vol): {naive_volatility:.2%}")
print(f"True Portfolio Volatility: {portfolio_volatility:.2%}")
print(f"Diversification Benefit: {naive_volatility - portfolio_volatility:.2%}")

# Risk contribution analysis
print("\\n" + "="*60)
print("RISK CONTRIBUTION ANALYSIS")
print("="*60)

marginal_contributions = np.dot(covariance_matrix, weights)
risk_contributions = weights * marginal_contributions
percentage_contributions = risk_contributions / portfolio_variance

print("Risk Contributions:")
for i, asset in enumerate(assets):
    print(f"{asset:20} | Marginal: {marginal_contributions[i]:7.4f} | Contribution: {percentage_contributions[i]:6.1%}")

print(f"\\nVerification - Sum of contributions: {np.sum(percentage_contributions):.1%}")

print("\\n✅ Try modifying the correlations or weights above!")
print("💡 Notice how negative correlations reduce portfolio risk")`}
          </InteractivePythonBlock>
        </section>

        {/* Chapter 3: Two-Asset Case */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-blue-500 pb-4">Chapter 3: The Two-Asset Case</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Before tackling multi-asset portfolios, it's essential to understand the two-asset case thoroughly. This simplified 
              scenario provides intuitive insights into diversification benefits and serves as the building block for more complex 
              portfolio optimization problems.
            </p>
          </div>

          {/* Two-Asset Formulas */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-blue-900 mb-6">📊 Two-Asset Portfolio Formulas</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-bold text-blue-800 mb-3">Expected Return:</h4>
                <div className="font-mono text-center text-lg bg-blue-50 p-3 rounded">
                  E(Rp) = wA × E(RA) + wB × E(RB)
                </div>
                <p className="text-sm text-blue-700 mt-2">Linear combination of individual returns</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-bold text-green-800 mb-3">Portfolio Variance:</h4>
                <div className="font-mono text-center text-lg bg-green-50 p-3 rounded">
                  σp² = wA²σA² + wB²σB² + 2wAwBσAσBρAB
                </div>
                <p className="text-sm text-green-700 mt-2">Includes covariance term for diversification</p>
              </div>
            </div>
            
            <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-bold text-yellow-800 mb-2">🔑 Key Insight:</h4>
              <p className="text-yellow-800">
                The portfolio variance includes a covariance term (2wAwBσAσBρAB) that can be negative when ρAB &lt; 0, 
                creating diversification benefits. This is why portfolio risk is not simply the weighted average of individual risks.
              </p>
            </div>
          </div>

          {/* Interactive Example 2: Two-Asset Analysis */}
          <InteractivePythonBlock 
            filename="two_asset_portfolio_analysis.py" 
            title="Complete Two-Asset Portfolio Analysis"
            editable={true}
          >
{`import numpy as np
import matplotlib.pyplot as plt

print("=== TWO-ASSET PORTFOLIO ANALYSIS ===")
print()

# Define two assets
asset_A = {"name": "Stock A", "return": 0.12, "volatility": 0.20}
asset_B = {"name": "Bond B", "return": 0.04, "volatility": 0.08}

print("Asset Characteristics:")
print(f"{asset_A['name']}: Return = {asset_A['return']:.1%}, Volatility = {asset_A['volatility']:.1%}")
print(f"{asset_B['name']}: Return = {asset_B['return']:.1%}, Volatility = {asset_B['volatility']:.1%}")
print()

# Analyze different correlation scenarios
correlations = [-0.5, 0.0, 0.3, 0.7, 1.0]
print("DIVERSIFICATION ANALYSIS ACROSS CORRELATIONS")
print("=" * 60)

for rho in correlations:
    print(f"\\nCorrelation ρ = {rho:4.1f}")
    print("-" * 25)
    
    # Generate efficient frontier for this correlation
    weights_A = np.linspace(0, 1, 11)  # 0% to 100% in Asset A
    
    results = []
    for w_A in weights_A:
        w_B = 1 - w_A
        
        # Portfolio return
        port_return = w_A * asset_A["return"] + w_B * asset_B["return"]
        
        # Portfolio variance
        port_variance = (w_A**2 * asset_A["volatility"]**2 + 
                        w_B**2 * asset_B["volatility"]**2 + 
                        2 * w_A * w_B * asset_A["volatility"] * asset_B["volatility"] * rho)
        
        port_volatility = np.sqrt(port_variance)
        
        results.append({
            'weight_A': w_A,
            'weight_B': w_B,
            'return': port_return,
            'volatility': port_volatility
        })
    
    # Find minimum variance portfolio
    min_var_idx = min(range(len(results)), key=lambda i: results[i]['volatility'])
    min_var_portfolio = results[min_var_idx]
    
    print(f"Minimum Variance Portfolio:")
    print(f"  Weights: {min_var_portfolio['weight_A']:.1%} Stock A, {min_var_portfolio['weight_B']:.1%} Bond B")
    print(f"  Return: {min_var_portfolio['return']:.2%}")
    print(f"  Volatility: {min_var_portfolio['volatility']:.2%}")
    
    # Compare to naive diversification (50/50)
    naive_return = 0.5 * asset_A["return"] + 0.5 * asset_B["return"]
    naive_volatility_weighted = 0.5 * asset_A["volatility"] + 0.5 * asset_B["volatility"]
    
    naive_variance = (0.5**2 * asset_A["volatility"]**2 + 
                     0.5**2 * asset_B["volatility"]**2 + 
                     2 * 0.5 * 0.5 * asset_A["volatility"] * asset_B["volatility"] * rho)
    naive_volatility_true = np.sqrt(naive_variance)
    
    diversification_benefit = naive_volatility_weighted - naive_volatility_true
    
    print(f"  50/50 Portfolio:")
    print(f"    Return: {naive_return:.2%}")
    print(f"    Volatility (weighted avg): {naive_volatility_weighted:.2%}")
    print(f"    Volatility (true): {naive_volatility_true:.2%}")
    print(f"    Diversification Benefit: {diversification_benefit:.2%}")

# Special cases analysis
print("\\n" + "=" * 60)
print("SPECIAL CASES ANALYSIS")
print("=" * 60)

special_cases = [
    {"rho": 1.0, "description": "Perfect Positive Correlation", "implication": "No diversification benefit"},
    {"rho": 0.0, "description": "Zero Correlation", "implication": "Moderate diversification benefit"},
    {"rho": -1.0, "description": "Perfect Negative Correlation", "implication": "Maximum diversification (risk can be eliminated)"}
]

for case in special_cases:
    rho = case["rho"]
    print(f"\\n{case['description']} (ρ = {rho:4.1f}):")
    print(f"Implication: {case['implication']}")
    
    if rho == -1.0:
        # Calculate risk-free combination
        # For perfect negative correlation, we can find weights that eliminate risk
        # σp² = wA²σA² + (1-wA)²σB² + 2wA(1-wA)σAσB(-1) = 0
        # Solving: wA = σB / (σA + σB)
        
        w_A_riskfree = asset_B["volatility"] / (asset_A["volatility"] + asset_B["volatility"])
        w_B_riskfree = 1 - w_A_riskfree
        
        riskfree_return = w_A_riskfree * asset_A["return"] + w_B_riskfree * asset_B["return"]
        
        print(f"  Risk-free portfolio weights: {w_A_riskfree:.1%} Stock A, {w_B_riskfree:.1%} Bond B")
        print(f"  Risk-free portfolio return: {riskfree_return:.2%}")
        print(f"  Portfolio volatility: 0.0% (risk eliminated!)")

print("\\n" + "=" * 60)
print("KEY INSIGHTS FROM TWO-ASSET ANALYSIS")
print("=" * 60)

insights = [
    "1. Portfolio return is always a weighted average of individual returns",
    "2. Portfolio risk is NOT a weighted average when correlation < 1",
    "3. Diversification benefit increases as correlation decreases",
    "4. Perfect negative correlation allows complete risk elimination",
    "5. The minimum variance portfolio is rarely 50/50 weighted",
    "6. Lower correlation assets should receive higher weights for risk reduction"
]

for insight in insights:
    print(insight)

print("\\n✅ Try changing the asset returns, volatilities, or correlations above!")
print("💡 Observe how the minimum variance portfolio weights change with correlation")`}
          </InteractivePythonBlock>
        </section>

        {/* Chapter 4: Multi-Asset Optimization */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-blue-500 pb-4">Chapter 4: Multi-Asset Portfolio Optimization</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Moving from two assets to multiple assets significantly increases the complexity of portfolio optimization, but also 
              greatly expands the diversification opportunities. With n assets, we have n(n-1)/2 unique correlations to consider, 
              creating a rich set of possible portfolio combinations.
            </p>
          </div>

          {/* Optimization Framework */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-green-900 mb-6">🎯 Portfolio Optimization Framework</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-bold text-green-800 mb-4">Objective Function:</h4>
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded font-mono text-center">
                    Minimize: ½ w'Σw
                  </div>
                  <div className="bg-blue-50 p-3 rounded font-mono text-center">
                    Subject to: μ'w = μp
                  </div>
                  <div className="bg-yellow-50 p-3 rounded font-mono text-center">
                    1'w = 1
                  </div>
                </div>
                <p className="text-sm text-green-700 mt-3">
                  Minimize portfolio variance for a target return level
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-bold text-blue-800 mb-4">Matrix Notation:</h4>
                <ul className="space-y-2 text-sm text-blue-700">
                  <li><strong>w</strong> = vector of portfolio weights</li>
                  <li><strong>Σ</strong> = covariance matrix (n × n)</li>
                  <li><strong>μ</strong> = vector of expected returns</li>
                  <li><strong>μp</strong> = target portfolio return</li>
                  <li><strong>1</strong> = vector of ones (sum constraint)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Interactive Example 3: Multi-Asset Optimization */}
          <InteractivePythonBlock 
            filename="multi_asset_portfolio_optimization.py" 
            title="Multi-Asset Portfolio Optimization with Scipy"
            editable={true}
          >
{`import numpy as np
import pandas as pd
from scipy.optimize import minimize
import warnings
warnings.filterwarnings('ignore')

print("=== MULTI-ASSET PORTFOLIO OPTIMIZATION ===")
print()

# Define a realistic 5-asset universe
assets = ['US Large Cap', 'US Small Cap', 'International', 'Emerging Markets', 'Bonds']
n_assets = len(assets)

# Expected returns (annual)
expected_returns = np.array([0.10, 0.12, 0.08, 0.14, 0.04])

# Volatilities (annual)
volatilities = np.array([0.16, 0.24, 0.18, 0.28, 0.06])

# Realistic correlation matrix
correlation_matrix = np.array([
    [1.00, 0.85, 0.75, 0.65, 0.20],  # US Large Cap
    [0.85, 1.00, 0.70, 0.70, 0.15],  # US Small Cap
    [0.75, 0.70, 1.00, 0.80, 0.25],  # International
    [0.65, 0.70, 0.80, 1.00, 0.10],  # Emerging Markets
    [0.20, 0.15, 0.25, 0.10, 1.00]   # Bonds
])

# Calculate covariance matrix
covariance_matrix = np.outer(volatilities, volatilities) * correlation_matrix

print("Asset Universe:")
print("-" * 70)
for i, asset in enumerate(assets):
    print(f"{asset:15} | Return: {expected_returns[i]:6.1%} | Volatility: {volatilities[i]:6.1%}")

print("\\nCorrelation Matrix:")
print("-" * 70)
corr_df = pd.DataFrame(correlation_matrix, index=assets, columns=[a[:8] for a in assets])
print(corr_df.round(2))

def portfolio_stats(weights, returns, cov_matrix):
    """Calculate portfolio return and risk"""
    port_return = np.sum(weights * returns)
    port_variance = np.dot(weights, np.dot(cov_matrix, weights))
    port_volatility = np.sqrt(port_variance)
    return port_return, port_volatility, port_variance

def minimize_volatility_for_return(target_return, returns, cov_matrix):
    """Find minimum volatility portfolio for a given target return"""
    n = len(returns)
    
    # Objective function: minimize portfolio variance
    def objective(weights):
        return np.dot(weights, np.dot(cov_matrix, weights))
    
    # Constraints
    constraints = [
        {'type': 'eq', 'fun': lambda w: np.sum(w) - 1},  # Weights sum to 1
        {'type': 'eq', 'fun': lambda w: np.sum(w * returns) - target_return}  # Target return
    ]
    
    # Bounds (long-only)
    bounds = [(0, 1) for _ in range(n)]
    
    # Initial guess
    x0 = np.array([1/n] * n)
    
    # Optimize
    result = minimize(objective, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    
    return result.x if result.success else None

# Generate efficient frontier
print("\\n" + "=" * 70)
print("EFFICIENT FRONTIER CALCULATION")
print("=" * 70)

min_return = min(expected_returns)
max_return = max(expected_returns)
target_returns = np.linspace(min_return, max_return, 15)

efficient_portfolios = []
print("\\nEfficient Frontier Portfolios:")
print("-" * 100)
print(f"{'Target Return':>12} | {'Volatility':>10} | {'Sharpe':>7} | {'US LC':>6} | {'US SC':>6} | {'Intl':>6} | {'EM':>6} | {'Bonds':>6}")
print("-" * 100)

risk_free_rate = 0.03

for target_ret in target_returns:
    weights = minimize_volatility_for_return(target_ret, expected_returns, covariance_matrix)
    
    if weights is not None:
        port_ret, port_vol, port_var = portfolio_stats(weights, expected_returns, covariance_matrix)
        sharpe_ratio = (port_ret - risk_free_rate) / port_vol
        
        efficient_portfolios.append({
            'return': port_ret,
            'volatility': port_vol,
            'sharpe': sharpe_ratio,
            'weights': weights.copy()
        })
        
        print(f"{port_ret:>11.1%} | {port_vol:>9.1%} | {sharpe_ratio:>6.2f} | " + 
              " | ".join([f"{w:>5.1%}" for w in weights]))

# Find special portfolios
print("\\n" + "=" * 70)
print("SPECIAL PORTFOLIOS")
print("=" * 70)

# 1. Minimum Variance Portfolio
def minimize_variance_only(returns, cov_matrix):
    n = len(returns)
    
    def objective(weights):
        return np.dot(weights, np.dot(cov_matrix, weights))
    
    constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
    bounds = [(0, 1) for _ in range(n)]
    x0 = np.array([1/n] * n)
    
    result = minimize(objective, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    return result.x if result.success else None

min_var_weights = minimize_variance_only(expected_returns, covariance_matrix)
if min_var_weights is not None:
    min_var_ret, min_var_vol, _ = portfolio_stats(min_var_weights, expected_returns, covariance_matrix)
    print(f"\\nMinimum Variance Portfolio:")
    print(f"  Return: {min_var_ret:.2%}, Volatility: {min_var_vol:.2%}")
    print("  Weights:")
    for i, (asset, weight) in enumerate(zip(assets, min_var_weights)):
        print(f"    {asset:15}: {weight:6.1%}")

# 2. Maximum Sharpe Ratio Portfolio
def maximize_sharpe_ratio(returns, cov_matrix, risk_free_rate):
    n = len(returns)
    
    def negative_sharpe(weights):
        port_ret, port_vol, _ = portfolio_stats(weights, returns, cov_matrix)
        return -(port_ret - risk_free_rate) / port_vol
    
    constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
    bounds = [(0, 1) for _ in range(n)]
    x0 = np.array([1/n] * n)
    
    result = minimize(negative_sharpe, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    return result.x if result.success else None

max_sharpe_weights = maximize_sharpe_ratio(expected_returns, covariance_matrix, risk_free_rate)
if max_sharpe_weights is not None:
    max_sharpe_ret, max_sharpe_vol, _ = portfolio_stats(max_sharpe_weights, expected_returns, covariance_matrix)
    max_sharpe_ratio = (max_sharpe_ret - risk_free_rate) / max_sharpe_vol
    print(f"\\nMaximum Sharpe Ratio Portfolio:")
    print(f"  Return: {max_sharpe_ret:.2%}, Volatility: {max_sharpe_vol:.2%}")
    print(f"  Sharpe Ratio: {max_sharpe_ratio:.3f}")
    print("  Weights:")
    for i, (asset, weight) in enumerate(zip(assets, max_sharpe_weights)):
        print(f"    {asset:15}: {weight:6.1%}")

# 3. Equal Weight Portfolio (for comparison)
equal_weights = np.array([1/n_assets] * n_assets)
equal_ret, equal_vol, _ = portfolio_stats(equal_weights, expected_returns, covariance_matrix)
equal_sharpe = (equal_ret - risk_free_rate) / equal_vol

print(f"\\nEqual Weight Portfolio (Benchmark):")
print(f"  Return: {equal_ret:.2%}, Volatility: {equal_vol:.2%}")
print(f"  Sharpe Ratio: {equal_sharpe:.3f}")
print("  Weights: 20.0% each asset")

# Risk contribution analysis for max Sharpe portfolio
print("\\n" + "=" * 70)
print("RISK CONTRIBUTION ANALYSIS (Max Sharpe Portfolio)")
print("=" * 70)

if max_sharpe_weights is not None:
    marginal_contributions = np.dot(covariance_matrix, max_sharpe_weights)
    risk_contributions = max_sharpe_weights * marginal_contributions
    total_risk_contribution = np.sum(risk_contributions)
    percentage_contributions = risk_contributions / total_risk_contribution
    
    print("\\nRisk Contributions:")
    for i, asset in enumerate(assets):
        print(f"{asset:15} | Weight: {max_sharpe_weights[i]:6.1%} | Risk Contrib: {percentage_contributions[i]:6.1%}")
    
    print(f"\\nVerification - Sum of risk contributions: {np.sum(percentage_contributions):.1%}")

print("\\n" + "=" * 70)
print("KEY INSIGHTS")
print("=" * 70)
print("1. Efficient portfolios concentrate in higher Sharpe ratio assets")
print("2. Minimum variance portfolio heavily weights low-volatility assets (bonds)")
print("3. Maximum Sharpe ratio portfolio balances return and risk optimally")
print("4. Diversification benefits are substantial with 5 assets vs individual holdings")
print("5. Risk contributions don't always match portfolio weights")

print("\\n✅ Try modifying expected returns, volatilities, or correlations!")
print("💡 Notice how small changes in inputs can significantly affect optimal weights")`}
          </InteractivePythonBlock>
        </section>

        {/* Chapter 5: Efficient Frontier */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 border-b-2 border-blue-500 pb-4">Chapter 5: The Efficient Frontier</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The efficient frontier represents one of the most important concepts in modern portfolio theory. It is the set of 
              portfolios that offer the highest expected return for each level of risk, or equivalently, the lowest risk for 
              each level of expected return. No rational investor would choose a portfolio that lies below the efficient frontier.
            </p>
          </div>

          {/* Efficient Frontier Properties */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-purple-900 mb-6">📈 Properties of the Efficient Frontier</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-bold text-purple-800 mb-2">Mathematical Properties:</h4>
                  <ul className="space-y-1 text-sm text-purple-700">
                    <li>• Concave (curved) shape in mean-variance space</li>
                    <li>• Smooth and continuous curve</li>
                    <li>• Asymptotes to minimum variance portfolio</li>
                    <li>• Extends infinitely upward (no maximum return)</li>
                    <li>• Hyperbolic in mathematical form</li>
                  </ul>
                </div>
                
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-bold text-blue-800 mb-2">Investment Properties:</h4>
                  <ul className="space-y-1 text-sm text-blue-700">
                    <li>• Represents optimal risk-return trade-offs</li>
                    <li>• All points are mean-variance efficient</li>
                    <li>• Higher expected return requires higher risk</li>
                    <li>• Dominates all other portfolio combinations</li>
                    <li>• Forms basis for capital allocation decisions</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-bold text-green-800 mb-4">Key Points on the Frontier:</h4>
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded">
                    <strong>Minimum Variance Portfolio (MVP):</strong>
                    <br />
                    <span className="text-sm">Leftmost point - lowest possible risk</span>
                  </div>
                  <div className="bg-blue-50 p-3 rounded">
                    <strong>Maximum Sharpe Ratio Portfolio:</strong>
                    <br />
                    <span className="text-sm">Tangent from risk-free rate - optimal risk premium per unit risk</span>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded">
                    <strong>Individual Assets:</strong>
                    <br />
                    <span className="text-sm">Usually lie below or on the frontier (rarely optimal alone)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Example 4: Efficient Frontier Visualization */}
          <InteractivePythonBlock 
            filename="efficient_frontier_visualization.py" 
            title="Efficient Frontier Analysis and Visualization"
            editable={true}
          >
{`import numpy as np
import pandas as pd
from scipy.optimize import minimize
import matplotlib.pyplot as plt

print("=== EFFICIENT FRONTIER COMPREHENSIVE ANALYSIS ===")
print()

# Enhanced asset universe with more realistic data
assets = {
    'US_Large_Cap': {'return': 0.10, 'volatility': 0.15, 'sharpe': 0.47},
    'US_Small_Cap': {'return': 0.12, 'volatility': 0.22, 'sharpe': 0.41},
    'International': {'return': 0.08, 'volatility': 0.17, 'sharpe': 0.29},
    'Emerging_Mkts': {'return': 0.13, 'volatility': 0.25, 'sharpe': 0.40},
    'REITs': {'return': 0.09, 'volatility': 0.19, 'sharpe': 0.32},
    'Commodities': {'return': 0.07, 'volatility': 0.21, 'sharpe': 0.19},
    'Bonds': {'return': 0.04, 'volatility': 0.04, 'sharpe': 0.25},
    'TIPS': {'return': 0.035, 'volatility': 0.06, 'sharpe': -0.08}
}

asset_names = list(assets.keys())
n_assets = len(asset_names)
expected_returns = np.array([assets[name]['return'] for name in asset_names])
volatilities = np.array([assets[name]['volatility'] for name in asset_names])

print("Extended Asset Universe:")
print("-" * 80)
print(f"{'Asset':<15} | {'Return':<8} | {'Volatility':<10} | {'Sharpe':<8}")
print("-" * 80)
for name in asset_names:
    asset = assets[name]
    print(f"{name:<15} | {asset['return']:<7.1%} | {asset['volatility']:<9.1%} | {asset['sharpe']:<7.2f}")

# Create a realistic correlation matrix
np.random.seed(42)  # For reproducibility
base_corr = 0.3  # Base correlation between risk assets
correlation_matrix = np.full((n_assets, n_assets), base_corr)
np.fill_diagonal(correlation_matrix, 1.0)

# Customize specific correlations for realism
correlation_adjustments = {
    ('US_Large_Cap', 'US_Small_Cap'): 0.85,
    ('US_Large_Cap', 'International'): 0.75,
    ('International', 'Emerging_Mkts'): 0.80,
    ('REITs', 'US_Large_Cap'): 0.65,
    ('Commodities', 'TIPS'): 0.45,
    ('Bonds', 'TIPS'): 0.70,
    ('Bonds', 'US_Large_Cap'): -0.10,
    ('Bonds', 'Commodities'): -0.15,
    ('TIPS', 'US_Large_Cap'): -0.05
}

# Apply correlation adjustments
for (asset1, asset2), corr in correlation_adjustments.items():
    i, j = asset_names.index(asset1), asset_names.index(asset2)
    correlation_matrix[i, j] = correlation_matrix[j, i] = corr

# Calculate covariance matrix
covariance_matrix = np.outer(volatilities, volatilities) * correlation_matrix

print("\\nSample Correlations:")
print("-" * 50)
sample_pairs = [('US_Large_Cap', 'Bonds'), ('US_Large_Cap', 'Emerging_Mkts'), 
                ('Bonds', 'Commodities'), ('REITs', 'US_Large_Cap')]
for asset1, asset2 in sample_pairs:
    i, j = asset_names.index(asset1), asset_names.index(asset2)
    print(f"{asset1} - {asset2}: {correlation_matrix[i,j]:6.2f}")

def portfolio_performance(weights, returns, cov_matrix, risk_free_rate=0.03):
    """Calculate comprehensive portfolio metrics"""
    port_return = np.sum(weights * returns)
    port_variance = np.dot(weights, np.dot(cov_matrix, weights))
    port_volatility = np.sqrt(port_variance)
    sharpe_ratio = (port_return - risk_free_rate) / port_volatility if port_volatility > 0 else 0
    
    return {
        'return': port_return,
        'volatility': port_volatility,
        'variance': port_variance,
        'sharpe': sharpe_ratio
    }

def efficient_frontier_portfolio(target_return, returns, cov_matrix):
    """Find efficient portfolio for target return"""
    n = len(returns)
    
    def objective(weights):
        return np.dot(weights, np.dot(cov_matrix, weights))
    
    constraints = [
        {'type': 'eq', 'fun': lambda w: np.sum(w) - 1},
        {'type': 'eq', 'fun': lambda w: np.sum(w * returns) - target_return}
    ]
    
    bounds = [(0, 1) for _ in range(n)]
    x0 = np.array([1/n] * n)
    
    result = minimize(objective, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    return result.x if result.success else None

# Generate comprehensive efficient frontier
print("\\n" + "=" * 80)
print("EFFICIENT FRONTIER GENERATION")
print("=" * 80)

risk_free_rate = 0.03
min_return = min(expected_returns) + 0.001  # Slightly above minimum
max_return = max(expected_returns) - 0.001  # Slightly below maximum
n_points = 25

target_returns = np.linspace(min_return, max_return, n_points)
efficient_portfolios = []

print(f"\\nGenerating {n_points} efficient portfolios...")
print("Target Return | Volatility | Sharpe | Top 3 Holdings")
print("-" * 65)

for i, target_ret in enumerate(target_returns):
    weights = efficient_frontier_portfolio(target_ret, expected_returns, covariance_matrix)
    
    if weights is not None:
        perf = portfolio_performance(weights, expected_returns, covariance_matrix, risk_free_rate)
        
        # Find top 3 holdings
        top_indices = np.argsort(weights)[-3:][::-1]
        top_holdings = [(asset_names[idx], weights[idx]) for idx in top_indices if weights[idx] > 0.01]
        top_str = ", ".join([f"{name}:{weight:.0%}" for name, weight in top_holdings[:3]])
        
        efficient_portfolios.append({
            'target_return': target_ret,
            'weights': weights,
            'performance': perf
        })
        
        if i % 5 == 0:  # Print every 5th portfolio to avoid clutter
            print(f"{perf['return']:>12.1%} | {perf['volatility']:>9.1%} | {perf['sharpe']:>5.2f} | {top_str}")

# Find and analyze special portfolios
print("\\n" + "=" * 80)
print("SPECIAL PORTFOLIO ANALYSIS")
print("=" * 80)

# 1. Global Minimum Variance Portfolio
def find_min_variance_portfolio(returns, cov_matrix):
    n = len(returns)
    
    def objective(weights):
        return np.dot(weights, np.dot(cov_matrix, weights))
    
    constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
    bounds = [(0, 1) for _ in range(n)]
    x0 = np.array([1/n] * n)
    
    result = minimize(objective, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    return result.x if result.success else None

gmv_weights = find_min_variance_portfolio(expected_returns, covariance_matrix)
if gmv_weights is not None:
    gmv_perf = portfolio_performance(gmv_weights, expected_returns, covariance_matrix, risk_free_rate)
    
    print("\\n1. GLOBAL MINIMUM VARIANCE PORTFOLIO:")
    print(f"   Return: {gmv_perf['return']:.2%}")
    print(f"   Volatility: {gmv_perf['volatility']:.2%}")
    print(f"   Sharpe Ratio: {gmv_perf['sharpe']:.3f}")
    print("   Holdings (>1%):")
    for i, (name, weight) in enumerate(zip(asset_names, gmv_weights)):
        if weight > 0.01:
            print(f"     {name:<15}: {weight:6.1%}")

# 2. Maximum Sharpe Ratio Portfolio
def find_max_sharpe_portfolio(returns, cov_matrix, risk_free_rate):
    n = len(returns)
    
    def negative_sharpe(weights):
        perf = portfolio_performance(weights, returns, cov_matrix, risk_free_rate)
        return -perf['sharpe']
    
    constraints = [{'type': 'eq', 'fun': lambda w: np.sum(w) - 1}]
    bounds = [(0, 1) for _ in range(n)]
    x0 = np.array([1/n] * n)
    
    result = minimize(negative_sharpe, x0, method='SLSQP', bounds=bounds, constraints=constraints)
    return result.x if result.success else None

max_sharpe_weights = find_max_sharpe_portfolio(expected_returns, covariance_matrix, risk_free_rate)
if max_sharpe_weights is not None:
    max_sharpe_perf = portfolio_performance(max_sharpe_weights, expected_returns, covariance_matrix, risk_free_rate)
    
    print("\\n2. MAXIMUM SHARPE RATIO PORTFOLIO:")
    print(f"   Return: {max_sharpe_perf['return']:.2%}")
    print(f"   Volatility: {max_sharpe_perf['volatility']:.2%}")
    print(f"   Sharpe Ratio: {max_sharpe_perf['sharpe']:.3f}")
    print("   Holdings (>1%):")
    for i, (name, weight) in enumerate(zip(asset_names, max_sharpe_weights)):
        if weight > 0.01:
            print(f"     {name:<15}: {weight:6.1%}")

# 3. Equal Weight Portfolio (benchmark)
equal_weights = np.array([1/n_assets] * n_assets)
equal_perf = portfolio_performance(equal_weights, expected_returns, covariance_matrix, risk_free_rate)

print("\\n3. EQUAL WEIGHT PORTFOLIO (Benchmark):")
print(f"   Return: {equal_perf['return']:.2%}")
print(f"   Volatility: {equal_perf['volatility']:.2%}")
print(f"   Sharpe Ratio: {equal_perf['sharpe']:.3f}")
print(f"   Holdings: {100/n_assets:.1f}% each asset")

# Efficient frontier statistics
if efficient_portfolios:
    frontier_returns = [p['performance']['return'] for p in efficient_portfolios]
    frontier_vols = [p['performance']['volatility'] for p in efficient_portfolios]
    frontier_sharpes = [p['performance']['sharpe'] for p in efficient_portfolios]
    
    print("\\n" + "=" * 80)
    print("EFFICIENT FRONTIER STATISTICS")
    print("=" * 80)
    print(f"Return Range: {min(frontier_returns):.1%} to {max(frontier_returns):.1%}")
    print(f"Volatility Range: {min(frontier_vols):.1%} to {max(frontier_vols):.1%}")
    print(f"Sharpe Ratio Range: {min(frontier_sharpes):.2f} to {max(frontier_sharpes):.2f}")
    
    # Find the portfolio with highest Sharpe ratio on frontier
    best_sharpe_idx = frontier_sharpes.index(max(frontier_sharpes))
    best_portfolio = efficient_portfolios[best_sharpe_idx]
    print(f"\\nBest Frontier Portfolio (Sharpe = {max(frontier_sharpes):.3f}):")
    print(f"  Return: {best_portfolio['performance']['return']:.2%}")
    print(f"  Volatility: {best_portfolio['performance']['volatility']:.2%}")

# Risk-Return Analysis
print("\\n" + "=" * 80)
print("RISK-RETURN ANALYSIS")
print("=" * 80)

print("\\nIndividual Asset Risk-Return:")
print("-" * 50)
for i, name in enumerate(asset_names):
    individual_sharpe = (expected_returns[i] - risk_free_rate) / volatilities[i]
    print(f"{name:<15}: {expected_returns[i]:6.1%} return, {volatilities[i]:6.1%} vol, {individual_sharpe:6.2f} Sharpe")

print("\\nDiversification Benefits:")
print("-" * 50)
if gmv_weights is not None and max_sharpe_weights is not None:
    min_individual_vol = min(volatilities)
    gmv_vol = gmv_perf['volatility']
    diversification_benefit = min_individual_vol - gmv_vol
    
    print(f"Lowest individual asset volatility: {min_individual_vol:.2%}")
    print(f"Global minimum variance portfolio: {gmv_vol:.2%}")
    print(f"Diversification benefit: {diversification_benefit:.2%}")
    print(f"Risk reduction: {diversification_benefit/min_individual_vol:.1%}")

print("\\n" + "=" * 80)
print("KEY INSIGHTS")
print("=" * 80)
insights = [
    "1. The efficient frontier is concave, showing diminishing returns to risk",
    "2. Minimum variance portfolio heavily weights low-volatility assets",
    "3. Maximum Sharpe portfolio balances return and risk optimally",
    "4. Diversification can reduce risk below any individual asset",
    "5. Most efficient portfolios avoid extreme positions",
    "6. Higher expected returns require disproportionately higher risk",
    "7. The tangent portfolio (max Sharpe) is optimal for leveraged investors"
]

for insight in insights:
    print(insight)

print("\\n✅ Try modifying the asset characteristics or correlations!")
print("💡 Notice how the efficient frontier shape changes with different inputs")`}
          </InteractivePythonBlock>
        </section>

        {/* Purchase CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-12 rounded-lg text-center mb-16">
          <h3 className="text-3xl font-bold mb-6">Complete Modern Portfolio Theory Mastery</h3>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            This is just a preview! The complete module includes 15+ interactive Python examples, advanced topics like 
            Black-Litterman model, risk budgeting, factor models, real-world case studies, and downloadable code.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white bg-opacity-20 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">📚 Complete Content</h4>
              <p className="text-sm opacity-90">16 comprehensive chapters with theory and practice</p>
            </div>
            <div className="bg-white bg-opacity-20 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">🐍 Interactive Python</h4>
              <p className="text-sm opacity-90">15+ fully executable code examples with real data</p>
            </div>
            <div className="bg-white bg-opacity-20 p-6 rounded-lg">
              <h4 className="font-bold text-lg mb-2">💼 Case Studies</h4>
              <p className="text-sm opacity-90">Real-world portfolio construction examples</p>
            </div>
          </div>
          <button className="bg-white text-blue-600 px-12 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors shadow-lg">
            Get library access — /subscribe
          </button>
          <p className="text-sm opacity-75 mt-4">Instant download • Lifetime access • Watermarked to your license</p>
        </div>
      </div>
    </div>
  );
}